import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import { Mail01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import failedIcon from "shared/icons/status-icons/failed.svg";
import SuccessIcon from "shared/icons/status-icons/success-icon.svg?react";
import {
  CanadianCriminalReportState,
  CanadianCriminalReportSummary,
  VerificationNames
} from "shared/types";
import { Applicant } from "types/application";
import { Minus } from "untitledui-js-base";
import { ReportHeader } from "views/applications-id-report/components/ReportHeader";

interface CanadianCriminalReportSliceProps {
  applicant: Applicant;
  applicationId: string;
  reportState?: CanadianCriminalReportState;
  summary?: CanadianCriminalReportSummary;
}

const FailedStateBody = ({
  title,
  description,
  onContactSupport
}: {
  title: string;
  description: string;
  onContactSupport?: () => void;
}) => (
  <div className="mx-auto flex max-w-[531px] grow flex-col items-center justify-center gap-6">
    <div className="bg-danger-50 p-5 rounded-full">
      <img
        src={failedIcon}
        className="rotate-180"
        alt="danger exclamation mark"
        height={24}
        width={24}
      />
    </div>
    <div>
      <h2 className="text-center text-base font-bold text-content-500">
        {title}
      </h2>
      <p className="mt-2 text-center text-sm font-medium text-content-300">
        {description}
      </p>
    </div>
    <Show if={Boolean(onContactSupport)}>
      <Button variant={ButtonVariant.PRIMARY} onClick={onContactSupport}>
        Contact support
      </Button>
    </Show>
  </div>
);

const InProgressBody = ({
  email,
  invitedAt
}: {
  email: string;
  invitedAt?: string | null;
}) => {
  const invitedMoment = invitedAt ? moment(invitedAt) : null;

  return (
    <div className="mx-auto flex max-w-[432px] grow flex-col items-center justify-center gap-6">
      <div className="bg-warning-50 p-5 rounded-full">
        <HugeiconsIcon
          icon={Mail01Icon}
          size={24}
          className="text-warning-600"
        />
      </div>
      <div className="space-y-2 text-center">
        <h2 className="text-base font-bold text-content-1000">
          Applicant has been sent a link to complete their Canadian criminal
          background check
        </h2>
        <div className="text-sm font-medium text-content-300">
          <p>
            Email has been sent to{" "}
            <span className="text-content-1000">{email}</span>
          </p>
          {invitedMoment?.isValid() ? (
            <p>on {invitedMoment.format("DD MMM YYYY [at] hh:mma")}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const DoneBody = ({
  onOpenAdditionalDocuments
}: {
  onOpenAdditionalDocuments: () => void;
}) => (
  <div className="flex grow flex-col items-center justify-center gap-6">
    <div className="w-16 h-16 bg-success-50 rounded-full flex items-center justify-center">
      <SuccessIcon className="w-5 text-success-600" />
    </div>
    <div className="space-y-2 text-center max-w-[432px]">
      <h1 className="text-base font-bold text-content-1000">
        Criminal report (CA) was ran
      </h1>
      <p className="text-content-300 text-sm font-medium">
        Canadian criminal report was ran and a PDF is available for review in
        the additional documents section
      </p>
    </div>

    <Button
      variant={ButtonVariant.PRIMARY}
      size={ButtonSize.SM}
      onClick={onOpenAdditionalDocuments}
    >
      Open additional documents
    </Button>
  </div>
);

export const CanadianCriminalReportSlice = ({
  applicant,
  applicationId,
  reportState,
  summary
}: CanadianCriminalReportSliceProps) => {
  const navigate = useNavigate();
  const intercom = useIntercom();

  if (reportState === CanadianCriminalReportState.NotResident)
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-6">
        <div className="w-16 h-16 bg-[#1C1D2205] rounded-full flex items-center justify-center">
          <Minus className="stroke-content-100 w-4 h-4" />
        </div>
        <p className="text-content-300 text-sm font-medium my-2 text-center max-w-[432px]">
          Report was not ran because applicant wasn’t a current Canadian
          resident or had a previous Canadian address
        </p>
      </div>
    );

  const renderBody = () => {
    switch (reportState) {
      case CanadianCriminalReportState.InProgress:
        return (
          <InProgressBody
            email={applicant.email}
            invitedAt={summary?.invited_at}
          />
        );
      case CanadianCriminalReportState.Issue:
        return (
          <FailedStateBody
            title="There was an error retrieving report"
            description="Applicant has successfully completed the application, but we're encountering an error retrieving the report from the data provider. Please contact our support team to resolve this issue"
            onContactSupport={() => intercom.show()}
          />
        );
      case CanadianCriminalReportState.Expired:
        return (
          <FailedStateBody
            title="Canadian criminal background check request has expired"
            description="The applicant did not submit their Canadian criminal application in time, and the request expired"
          />
        );
      default:
        return (
          <DoneBody
            onOpenAdditionalDocuments={() =>
              navigate(
                `/applications/${applicationId}/reports?applicantId=${applicant.id}&report=additional_documents`
              )
            }
          />
        );
    }
  };

  return (
    <div className="w-full h-full flex flex-col">
      <ReportHeader
        title="Criminal report (CA)"
        name={VerificationNames.CanadianCriminalReport}
        reportDate={
          reportState === CanadianCriminalReportState.InProgress
            ? undefined
            : summary?.report_date || undefined
        }
        applicationId={applicationId}
        applicantId={applicant.id}
      />
      {renderBody()}
    </div>
  );
};
